import type { ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { Alert, Button } from 'react-bootstrap';
import { BsArrowLeft, BsSearch, BsX } from 'react-icons/bs';
import styles from './configurator.module.scss';
import { useConfiguratorTranslations } from './translations';
import type { Catalog, CatalogOption, Domain, OptionId } from './domain';

const FMT = new Intl.NumberFormat('it-IT', {
	style: 'currency',
	currency: 'EUR',
});

export function formatCurrency(n: number): string {
	return FMT.format(n);
}

export const stripCode = (label: string): string =>
	label.replace(/^\s*\[[^\]]*\]\s*/, '');

export const titleCase = (value: string | null | undefined): string => {
	const v = (value ?? '').trim();
	return v ? v.charAt(0).toUpperCase() + v.slice(1) : '';
};

type BackLinkProps = {
	onClick: () => void;
	children: ReactNode;
	className?: string;
};

export function BackLink({ onClick, children, className }: BackLinkProps) {
	return (
		<button
			type="button"
			className={`${styles.backLink} text-muted ${className ?? ''}`.trim()}
			onClick={onClick}
		>
			<BsArrowLeft className={styles.backLinkIcon} />
			{children}
		</button>
	);
}

interface SearchInputProps {
	value: string;
	onChange: (v: string) => void;
	placeholder?: string;
	className?: string;
}

export function ConfiguratorSearchInput({
	value,
	onChange,
	placeholder,
	className,
}: SearchInputProps) {
	return (
		<div className={`${styles.searchWrapper} ${className ?? ''}`}>
			<BsSearch className={styles.searchIcon} />
			<input
				className={styles.searchInput}
				placeholder={placeholder}
				value={value}
				onChange={(e) => onChange(e.target.value)}
			/>
			{value && (
				<button
					className={styles.searchClear}
					onClick={() => onChange('')}
					aria-label="Cancella ricerca"
				>
					<BsX size={18} />
				</button>
			)}
		</div>
	);
}

export function extractErrorMessage(error: unknown): string | null {
	if (!error) return null;
	const err = error as {
		body?: unknown;
		status?: number;
		statusText?: string;
		message?: string;
	};

	const body = err.body as
		| string
		| { message?: string; error?: string; detail?: string }
		| undefined;

	const fromBody =
		typeof body === 'string'
			? body
			: body?.message ?? body?.error ?? body?.detail ?? null;

	if (fromBody) return String(fromBody).trim();

	if (err.status) {
		const text = err.statusText || err.message;
		return text ? `${err.status} ${text}` : String(err.status);
	}

	if (error instanceof Error && error.message) return error.message;

	return null;
}

interface QueryStateBoundaryProps<T> {
	query: UseQueryResult<T>;
	skeleton: ReactNode;
	children: (data: T) => ReactNode;
}

export function QueryStateBoundary<T>({
	query,
	skeleton,
	children,
}: QueryStateBoundaryProps<T>) {
	const tr = useConfiguratorTranslations();

	if (query.isPending) {
		return <div className={styles.pending}>{skeleton}</div>;
	}

	if (query.isError) {
		const detail = extractErrorMessage(query.error);
		return (
			<Alert variant="danger" className={styles.error}>
				<div>{detail ? tr.loadingErrorDetail(detail) : tr.loadingError}</div>
				<Button
					size="sm"
					variant="outline-danger"
					className="ms-auto flex-shrink-0"
					onClick={() => query.refetch()}
				>
					{tr.retry}
				</Button>
			</Alert>
		);
	}

	return <>{children(query.data!)}</>;
}

export type Availability = 'selected' | 'locked' | 'blocked' | 'available';

export function availabilityOf(
	optionId: OptionId,
	committedPicks: OptionId[],
	domains: Map<OptionId, Domain>
): Availability {
	if (committedPicks.includes(optionId)) return 'selected';
	const domain = domains.get(optionId);
	if (domain === 'forced') return 'locked';
	if (domain === 'forbidden') return 'blocked';
	return 'available';
}

/**
 * Partiziona una lista di opzioni in quelle da mostrare e quelle `blocked`
 * (incompatibili con la selezione attuale) da nascondere di default. Solo lo
 * stato `blocked` viene nascosto: `selected`/`locked`/`available` restano
 * visibili. Nei gruppi multi-select (nessun conflitto) `hiddenBlocked` è vuoto,
 * quindi la UI non mostra alcun affordance di reveal.
 */
export function partitionByAvailability(
	options: CatalogOption[],
	committedPicks: OptionId[],
	domains: Map<OptionId, Domain>
): { visible: CatalogOption[]; hiddenBlocked: CatalogOption[] } {
	const visible: CatalogOption[] = [];
	const hiddenBlocked: CatalogOption[] = [];
	for (const o of options) {
		if (availabilityOf(o.id, committedPicks, domains) === 'blocked') {
			hiddenBlocked.push(o);
		} else {
			visible.push(o);
		}
	}
	return { visible, hiddenBlocked };
}

/**
 * Gli id dei gemelli tecnici collassati nel loro master (vernici): sono nel
 * catalogo/engine ma non vanno mostrati come voci a sé (anteprima conseguenze,
 * riepilogo). Vedi il collasso master/gemello in `normalize`.
 */
export function collapsedTwinIds(catalog: Catalog | null): Set<OptionId> {
	const ids = new Set<OptionId>();
	for (const o of catalog?.options ?? []) {
		if (o.twinId != null) ids.add(o.twinId);
	}
	return ids;
}
